import { useMemo, useState } from 'react'
import { View, Text, Pressable, Modal, ScrollView, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { fmtSeconds } from '../../lib/riderInsights'
import { NationalFlag, RiderName, stripTeamBracket, BibTag } from './ui'
import RiderFace from '../RiderFace'

// Scheda del corridore nella scelta delle pick: "Con te" (le tue scelte passate) e "Forma" (la sua stagione).
// rider: voce di allRiders; insight: riga di fn_rider_insights (o null mentre carica).
function Row({ label, value, tc, s }) {
  return (
    <View style={s.row}>
      <Text style={[s.rowLabel, { color: tc.textMuted }]}>{label}</Text>
      <Text style={[s.rowValue, { color: tc.text }]}>{value}</Text>
    </View>
  )
}

export default function RiderInsightSheet({ riders, insights, isNationalRace = false, onClose }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const s = useMemo(() => makeStyles(), [])
  const insets = useSafeAreaInsets()
  const [idx, setIdx] = useState(0)
  const rider = riders[Math.min(idx, riders.length - 1)]
  const i = insights?.[rider.value] ?? null
  const name = isNationalRace ? stripTeamBracket(rider.label_new || rider.label) : (rider.label_new || rider.label)
  const gap = i ? fmtSeconds(i.mine_avg_gap_s) : ''

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onClose}>
      <View style={s.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" accessibilityLabel={t('pick.close')} />
        <View style={[s.sheet, { backgroundColor: tc.bgCard, paddingBottom: insets.bottom + 12 }]}>
          <View style={[s.header, { borderBottomColor: tc.border }]}>
            <RiderFace avatar={rider.avatar} kit={isNationalRace ? null : rider.kit} size={52} shape="bust" />
            <BibTag bib={rider.bib} />
            {rider.id_national ? <NationalFlag code={rider.id_national} /> : null}
            <View style={{ flex: 1, minWidth: 0 }}>
              <RiderName name={name} style={[s.title, { color: tc.text }]} />
            </View>
            <Pressable onPress={onClose} hitSlop={6} style={[s.close, { backgroundColor: tc.bgCard2 }]} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
              <MaterialCommunityIcons name="close" size={18} color={tc.text} />
            </Pressable>
          </View>

          {riders.length > 1 && (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.chips} style={{ flexGrow: 0 }}>
              {riders.map((r, k) => {
                const on = k === idx
                return (
                  <Pressable
                    key={r.value}
                    onPress={() => setIdx(k)}
                    accessibilityRole="tab"
                    accessibilityState={{ selected: on }}
                    style={[s.chip, { backgroundColor: on ? tc.rosa : tc.bgCard2, borderColor: on ? tc.rosa : tc.border }]}
                  >
                    <Text style={[s.chipText, { color: on ? tc.brandText : tc.text }]} numberOfLines={1}>{k + 1}° {r.surname}</Text>
                  </Pressable>
                )
              })}
            </ScrollView>
          )}

          <ScrollView contentContainerStyle={{ padding: 14, gap: 14 }}>
            {!i ? (
              <Text style={[s.empty, { color: tc.textMuted }]}>{t('common.loading')}</Text>
            ) : (
              <>
                {/* Con te */}
                <View style={[s.card, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
                  <Text style={[s.cardTitle, { color: tc.rosaText }]}>{t('insight.you').toUpperCase()}</Text>
                  {i.mine_n === 0 ? (
                    <Text style={[s.never, { color: tc.text }]}>{t('insight.never')}</Text>
                  ) : (
                    <>
                      <Row s={s} tc={tc} label={t('insight.picked')} value={String(i.mine_n)} />
                      {i.mine_avg_rank != null && <Row s={s} tc={tc} label={t('insight.avg_rank')} value={`${i.mine_avg_rank}°`} />}
                      {!!gap && <Row s={s} tc={tc} label={t('insight.avg_gap')} value={gap} />}
                      {i.mine_best != null && <Row s={s} tc={tc} label={t('insight.best')} value={`${i.mine_best}°`} />}
                      {i.mine_dnf > 0 && <Row s={s} tc={tc} label={t('insight.dnf')} value={String(i.mine_dnf)} />}
                      {!!i.mine_last_race && (
                        <Row s={s} tc={tc} label={t('insight.last')} value={`${i.mine_last_race} · ${i.mine_last_rank != null ? `${i.mine_last_rank}°` : t('insight.retired')}`} />
                      )}
                    </>
                  )}
                </View>

                {/* Forma */}
                <View style={[s.card, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
                  <Text style={[s.cardTitle, { color: tc.rosaText }]}>{t('insight.form').toUpperCase()}</Text>
                  {i.season_n === 0 ? (
                    <Text style={[s.never, { color: tc.text }]}>{t('insight.no_results')}</Text>
                  ) : (
                    <>
                      {i.form_avg != null && <Row s={s} tc={tc} label={t('insight.form_avg')} value={`${i.form_avg}°`} />}
                      <Row s={s} tc={tc} label={t('insight.season_races')} value={String(i.season_n)} />
                      <Row s={s} tc={tc} label={t('insight.top10')} value={String(i.season_top10)} />
                      <Row s={s} tc={tc} label={t('insight.wins')} value={String(i.season_wins)} />
                      {i.season_dnf > 0 && <Row s={s} tc={tc} label={t('insight.dnfs')} value={String(i.season_dnf)} />}
                      {i.cat_n >= 2 && i.stage_cat != null && i.cat_avg != null && (
                        <Row s={s} tc={tc} label={t(`insight.cat_${i.stage_cat}`)} value={`${i.cat_avg}° (n=${i.cat_n})`} />
                      )}
                      {i.form?.length > 0 && (
                        <View style={{ marginTop: 8 }}>
                          <Text style={[s.sub, { color: tc.textMuted }]}>{t('insight.last5')}</Text>
                          {i.form.map((f, k) => (
                            <View key={k} style={s.formRow}>
                              <Text style={[s.formRace, { color: tc.text }]} numberOfLines={1}>{f.race}</Text>
                              <Text style={[s.formRank, { color: f.dnf ? tc.textMuted : (f.rank <= 10 ? tc.rosaText : tc.text) }]}>{f.dnf ? t('insight.retired') : `${f.rank}°`}</Text>
                            </View>
                          ))}
                        </View>
                      )}
                    </>
                  )}
                </View>
              </>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  )
}

const makeStyles = () => StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '82%' },
  header: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 14, borderBottomWidth: 0.5 },
  title: { fontSize: 18 },
  close: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  chips: { paddingHorizontal: 14, paddingVertical: 10, gap: 8 },
  chip: { minHeight: 44, paddingHorizontal: 14, borderRadius: 22, borderWidth: 1, alignItems: 'center', justifyContent: 'center', maxWidth: 180 },
  chipText: { fontFamily: fonts.condensedBold, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.3 },
  empty: { fontSize: 13, textAlign: 'center', paddingVertical: 24 },
  card: { borderWidth: 1, borderRadius: 14, padding: 12, gap: 6 },
  cardTitle: { fontFamily: fonts.condensedBold, fontSize: 12, letterSpacing: 1.5, marginBottom: 2 },
  never: { fontSize: 14, lineHeight: 20 },
  row: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  rowLabel: { fontSize: 13, flexShrink: 1 },
  rowValue: { fontFamily: fonts.condensedBold, fontSize: 15, flexShrink: 1, textAlign: 'right' },
  sub: { fontSize: 11, fontFamily: fonts.condensedBold, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 },
  formRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingVertical: 3 },
  formRace: { fontSize: 13, flex: 1 },
  formRank: { fontFamily: fonts.condensedBold, fontSize: 14 },
})
